import React from 'react';
import { Card, CardBody, CardHeader, Badge, Row, Col } from 'reactstrap';
import { AlertCircle, AlertTriangle, Calendar, FileText } from 'react-feather';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../../Context/AuthContext';
import { toast, ToastContainer } from 'react-toastify';

interface Alert {
    type: string;
    severity: string;
    message: string;
    date: string;
    vehicle_id?: number;
    driver_id?: number;
}

interface AlertsWidgetProps {
    alerts: Alert[];
}

export default function AlertsWidget({ alerts }: AlertsWidgetProps) {
    const navigate = useNavigate();
    const { hasPermission } = useAuth();

    const getIconConfig = (type: string) => {
        switch (type) {
            case 'insurance':
                return { icon: FileText, label: 'Insurance', color: '#3b82f6' };
            case 'rc':
                return { icon: FileText, label: 'RC Renewal', color: '#8b5cf6' };
            case 'license':
                return { icon: AlertCircle, label: 'License', color: '#f59e0b' };
            case 'next_service':
                return { icon: Calendar, label: 'Next Service', color: '#10b981' };
            case 'maintenance':
                return { icon: AlertTriangle, label: 'Overdue', color: '#ef4444' };
            default:
                return { icon: Calendar, label: 'Alert', color: '#6b7280' };
        }
    };

    const getSeverityColor = (severity: string) => {
        return severity === 'critical' ? 'danger' : 'warning';
    };

    const handleAlertClick = (alert: Alert) => {
        if (alert.type === 'insurance' && alert.vehicle_id) {
            if (hasPermission('insurance-renewal.view')) {
                navigate(`${process.env.PUBLIC_URL}/insurance_renewal`);
            } else {
                toast.error('Access Denied: You do not have permission to view Insurance Renewals.');
            }
        } else if (alert.type === 'rc' && alert.vehicle_id) {
            if (hasPermission('rc-renewal.view')) {
                navigate(`${process.env.PUBLIC_URL}/rc_renewal`);
            } else {
                toast.error('Access Denied: You do not have permission to view RC Renewals.');
            }
        } else if (alert.type === 'license' && alert.driver_id) {
            if (hasPermission('license-renewal.view')) {
                navigate(`${process.env.PUBLIC_URL}/license_renewal`);
            } else {
                toast.error('Access Denied: You do not have permission to view License Renewals.');
            }
        } else if ((alert.type === 'maintenance' || alert.type === 'next_service') && alert.vehicle_id) {
            if (hasPermission('maintenance.view')) {
                navigate(`${process.env.PUBLIC_URL}/maintenance`);
            } else {
                toast.error('Access Denied: You do not have permission to view Maintenance.');
            }
        }
    };

    const criticalAlerts = alerts.filter(a => a.severity === 'critical');
    const warningAlerts = alerts.filter(a => a.severity === 'warning');

    return (
        <Card className="chart-card h-100">
            <CardHeader className="pb-0">
                <div className="d-flex align-items-center justify-content-between">
                    <div>
                        <h5 className="mb-0">Alerts & Notifications</h5>
                        <p className="text-muted mb-0" style={{ fontSize: '0.875rem' }}>
                            <span className="text-danger fw-bold">{criticalAlerts.length} critical</span>
                            {', '}
                            <span className="text-warning fw-bold">{warningAlerts.length} warnings</span>
                        </p>
                    </div>
                    {alerts.length > 0 && (
                        <Badge
                            color="danger"
                            pill
                            style={{
                                fontSize: '0.875rem',
                                padding: '0.5rem 0.75rem',
                                boxShadow: '0 2px 8px rgba(220, 53, 69, 0.3)'
                            }}
                        >
                            {alerts.length}
                        </Badge>
                    )}
                </div>
            </CardHeader>
            <CardBody>
                {alerts.length === 0 ? (
                    <div className="empty-state">
                        <div className="empty-state-icon">
                            <AlertCircle size={36} className="text-success" />
                        </div>
                        <h6 className="text-success mb-2">All Clear!</h6>
                        <p className="text-muted mb-0">
                            No pending alerts or notifications
                        </p>
                    </div>
                ) : (
                    <div className="alerts-list">
                        <Row>
                            {alerts.slice(0, 10).map((alert, index) => {
                                const iconConfig = getIconConfig(alert.type);
                                const Icon = iconConfig.icon;
                                const severityColor = getSeverityColor(alert.severity);

                                return (
                                    <Col md={12} key={index} className="mb-3">
                                        <div
                                            className={`alert alert-${severityColor} alert-item d-flex align-items-start mb-0`}
                                            style={{
                                                cursor: 'pointer',
                                                animationDelay: `${index * 0.05}s`
                                            }}
                                            onClick={() => handleAlertClick(alert)}
                                            role="button"
                                        >
                                            <div className="alert-icon-wrapper me-3">
                                                <Icon size={20} className={`text-${severityColor}`} strokeWidth={2.5} />
                                            </div>
                                            <div className="flex-grow-1">
                                                <div className="d-flex align-items-start justify-content-between mb-1">
                                                    <span className={`badge bg-${severityColor}`} style={{ fontSize: '0.7rem' }}>
                                                        {iconConfig.label}
                                                    </span>
                                                    <small className="text-muted fw-bold">
                                                        {new Date(alert.date).toLocaleDateString('en-GB')}
                                                    </small>
                                                </div>
                                                <p className="mb-0" style={{ fontSize: '0.875rem', fontWeight: 500, lineHeight: '1.5', color: '#2d3748' }}>
                                                    {alert.message}
                                                </p>
                                            </div>
                                        </div>
                                    </Col>
                                );
                            })}
                        </Row>
                        {alerts.length > 10 && (
                            <div className="text-center mt-3 pt-3" style={{ borderTop: '1px solid #e2e8f0' }}>
                                <button
                                    className="btn btn-outline-primary btn-sm"
                                    onClick={() => {
                                        if (hasPermission('renewal-report.view')) {
                                            navigate(`${process.env.PUBLIC_URL}/renewal_report`);
                                        } else {
                                            toast.error('Access Denied: You do not have permission to view Renewal Reports.');
                                        }
                                    }}
                                >
                                    View All {alerts.length} Alerts
                                </button>
                            </div>
                        )}
                    </div>
                )}
            </CardBody>
            <ToastContainer />
        </Card>
    );
}
